import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiFetch, API_BASE } from "../../utils/api"; // use API_BASE
import { useSettingsSection } from "./SettingsContext";
import { SettingsSectionSkeleton } from "./SettingsSkeleton";

// Events we’re configuring
const EVENT_CONFIGS = [{
  key: "newTicket",
  label: "New ticket received"
}, {
  key: "addonReceived",
  label: "Addon received"
}, {
  key: "voidedItem",
  label: "Voided item"
}, {
  key: "courseFired",
  label: "Course fired"
}, {
  key: "ticketYellow",
  label: "Ticket turns yellow"
}, {
  key: "ticketRed",
  label: "Ticket turns red"
}, {
  key: "courseBumped",
  label: "Course bumped"
}, {
  key: "ticketBumped",
  label: "Ticket bumped"
}];
export default function SoundNotifications({
  stationId
}) {
  const [settings, setSettings] = useState({
    newTicket: {
      sound: "",
      durationMs: 1500
    },
    addonReceived: {
      sound: "",
      durationMs: 1500
    },
    voidedItem: {
      sound: "",
      durationMs: 1500
    },
    courseFired: {
      sound: "",
      durationMs: 1500
    },
    ticketYellow: {
      sound: "",
      durationMs: 1500
    },
    ticketRed: {
      sound: "",
      durationMs: 1500
    },
    courseBumped: {
      sound: "",
      durationMs: 1500
    },
    ticketBumped: {
      sound: "",
      durationMs: 1500
    }
  });
  const [soundOptions, setSoundOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const initialRef = useRef(null);
  const [previewingKey, setPreviewingKey] = useState(null);

  // upload-related state
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // keep a single audio instance + timeout for duration
  const audioRef = useRef(null);
  const timeoutRef = useRef(null);
  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        // 1) Current per-station sound settings
        const settingsRes = await apiFetch(`/settings/sounds/${stationId}`);
        const settingsData = await settingsRes.json();
        const nextSettings = {
          newTicket: {
            sound: settingsData.new_ticket_sound ?? settingsData.newTicketSound ?? settingsData.newTicket?.sound ?? "",
            durationMs: settingsData.new_ticket_duration_ms ?? settingsData.newTicketDurationMs ?? settingsData.newTicket?.durationMs ?? 1500
          },
          addonReceived: {
            sound: settingsData.addon_received_sound ?? settingsData.addonReceivedSound ?? settingsData.addonReceived?.sound ?? "",
            durationMs: settingsData.addon_received_duration_ms ?? settingsData.addonReceivedDurationMs ?? settingsData.addonReceived?.durationMs ?? 1500
          },
          voidedItem: {
            sound: settingsData.voided_item_sound ?? settingsData.voidedItemSound ?? settingsData.voidedItem?.sound ?? "",
            durationMs: settingsData.voided_item_duration_ms ?? settingsData.voidedItemDurationMs ?? settingsData.voidedItem?.durationMs ?? 1500
          },
          courseFired: {
            sound: settingsData.course_fired_sound ?? settingsData.courseFiredSound ?? settingsData.courseFired?.sound ?? "",
            durationMs: settingsData.course_fired_duration_ms ?? settingsData.courseFiredDurationMs ?? settingsData.courseFired?.durationMs ?? 1500
          },
          ticketYellow: {
            sound: settingsData.ticket_yellow_sound ?? settingsData.ticketYellowSound ?? settingsData.ticketYellow?.sound ?? "",
            durationMs: settingsData.ticket_yellow_duration_ms ?? settingsData.ticketYellowDurationMs ?? settingsData.ticketYellow?.durationMs ?? 1500
          },
          ticketRed: {
            sound: settingsData.ticket_red_sound ?? settingsData.ticketRedSound ?? settingsData.ticketRed?.sound ?? "",
            durationMs: settingsData.ticket_red_duration_ms ?? settingsData.ticketRedDurationMs ?? settingsData.ticketRed?.durationMs ?? 1500
          },
          ticketBumped: {
            sound: settingsData.ticket_bumped_sound ?? settingsData.ticketBumpedSound ?? settingsData.ticketBumped?.sound ?? "",
            durationMs: settingsData.ticket_bumped_duration_ms ?? settingsData.ticketBumpedDurationMs ?? settingsData.ticketBumped?.durationMs ?? 1500
          },
          courseBumped: {
            sound: settingsData.course_bumped_sound ?? settingsData.courseBumpedSound ?? settingsData.courseBumped?.sound ?? "",
            durationMs: settingsData.course_bumped_duration_ms ?? settingsData.courseBumpedDurationMs ?? settingsData.courseBumped?.durationMs ?? 1500
          }
        };
        setSettings(nextSettings);
        if (!initialRef.current) {
          initialRef.current = nextSettings;
        }

        // 2) Available sound options
        const optionsRes = await apiFetch("/settings/sounds/options");
        const optionsData = await optionsRes.json();
        setSoundOptions(optionsData || []);
      } catch (err) {
        console.error("Failed to fetch sound notification settings:", err);
      } finally {
        setLoading(false);
      }
    }
    if (stationId) {
      fetchData();
    }

    // cleanup on unmount
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [stationId]);
  const updateEventSetting = (eventKey, field, value) => {
    setSettings(prev => ({
      ...prev,
      [eventKey]: {
        ...prev[eventKey],
        [field]: field === "durationMs" ? Number(value) || 0 : value
      }
    }));
  };
  const saveToStation = useCallback(async (targetStationId, shouldUpdateInitial, overrideSettings) => {
    setSaving(true);
    try {
      const payloadSettings = overrideSettings || settings;
      await apiFetch(`/settings/sounds/${targetStationId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          new_ticket_sound: payloadSettings.newTicket.sound,
          new_ticket_duration_ms: payloadSettings.newTicket.durationMs,
          addon_received_sound: payloadSettings.addonReceived.sound,
          addon_received_duration_ms: payloadSettings.addonReceived.durationMs,
          voided_item_sound: payloadSettings.voidedItem.sound,
          voided_item_duration_ms: payloadSettings.voidedItem.durationMs,
          course_fired_sound: payloadSettings.courseFired.sound,
          course_fired_duration_ms: payloadSettings.courseFired.durationMs,
          ticket_yellow_sound: payloadSettings.ticketYellow.sound,
          ticket_yellow_duration_ms: payloadSettings.ticketYellow.durationMs,
          ticket_red_sound: payloadSettings.ticketRed.sound,
          ticket_red_duration_ms: payloadSettings.ticketRed.durationMs,
          ticket_bumped_sound: payloadSettings.ticketBumped.sound,
          ticket_bumped_duration_ms: payloadSettings.ticketBumped.durationMs,
          course_bumped_sound: payloadSettings.courseBumped.sound,
          course_bumped_duration_ms: payloadSettings.courseBumped.durationMs
        })
      });
      if (shouldUpdateInitial) {
        initialRef.current = payloadSettings;
      }
    } catch (err) {
      console.error("Failed to save sound notification settings:", err);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [settings]);
  const getChanges = useCallback(() => {
    if (!initialRef.current) return [];
    const changes = [];
    EVENT_CONFIGS.forEach(evt => {
      const prev = initialRef.current[evt.key] || {};
      const cur = settings[evt.key] || {};
      const soundChanged = prev.sound !== cur.sound;
      const durationChanged = Number(prev.durationMs) !== Number(cur.durationMs);
      if (soundChanged || durationChanged) {
        const parts = [];
        if (soundChanged) {
          parts.push(`sound "${prev.sound || "none"}" -> "${cur.sound || "none"}"`);
        }
        if (durationChanged) {
          parts.push(`duration ${Number(prev.durationMs || 0) / 1000}s -> ${Number(cur.durationMs || 0) / 1000}s`);
        }
        changes.push(`Sound notifications: ${evt.label} (${parts.join(", ")})`);
      }
    });
    return changes;
  }, [settings]);
  useSettingsSection("sound-notifications", useMemo(() => ({
    getChanges,
    save: () => saveToStation(stationId, true),
    applyToStation: targetStationId => saveToStation(targetStationId, false),
    applySavedToStation: targetStationId => {
      if (!initialRef.current) return Promise.resolve();
      return saveToStation(targetStationId, false, initialRef.current);
    }
  }), [getChanges, saveToStation, stationId]));

  // helper to stop any playing audio + clear timer
  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setPreviewingKey(null);
  };

  // Toggle preview / stop
  const handlePreview = async eventKey => {
    // If this event is already playing, clicking again stops it
    if (previewingKey === eventKey) {
      stopAudio();
      return;
    }
    const soundFile = settings[eventKey].sound;
    if (!soundFile) {
      alert("No sound selected to preview.");
      return;
    }
    const durationMs = settings[eventKey].durationMs || 0;
    try {
      // stop any previous audio first
      stopAudio();

      // Look up option to decide prefix (system vs uploaded)
      const opt = soundOptions.find(o => o.file === soundFile);

      // API_BASE = e.g. 
      const base = API_BASE.replace(/\/$/, ""); // strip trailing slash
      const pathPiece = opt?.type === "system" ? "/sounds" : "/uploads/sounds";

      // We exposed /api/uploads/sounds on the backend
      const src = `${base}${pathPiece}/${encodeURIComponent(soundFile)}`;
      const audio = new Audio(src);
      audioRef.current = audio;
      if (durationMs > 0) {
        // respect duration: loop audio, stop after durationMs
        audio.loop = true;
        timeoutRef.current = window.setTimeout(() => {
          // only stop if this key is still active
          setPreviewingKey(current => current === eventKey ? null : current);
          if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            audioRef.current = null;
          }
          timeoutRef.current = null;
        }, durationMs);

        // no need for onended, we control stop via timeout
        audio.onended = null;
      } else {
        // 0 = play full sound once
        audio.loop = false;
        audio.onended = () => {
          setPreviewingKey(current => current === eventKey ? null : current);
          audioRef.current = null;
        };
      }
      await audio.play();
      setPreviewingKey(eventKey);
    } catch (err) {
      console.error("Failed to play preview sound:", err);
      alert("Unable to play preview sound.");
      stopAudio();
    }
  };
  const handleUpload = async () => {
    if (!uploadFile) {
      alert("Please choose an audio file first.");
      return;
    }
    const formData = new FormData();
    formData.append("sound", uploadFile);
    try {
      setUploading(true);
      const res = await apiFetch("/settings/sounds/upload", {
        method: "POST",
        body: formData // browser sets proper multipart boundary
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Upload failed");
      }
      const optionsRes = await apiFetch("/settings/sounds/options");
      const optionsData = await optionsRes.json();
      setSoundOptions(optionsData || []);
      setUploadFile(null);
      alert(" Sound uploaded successfully!");
    } catch (err) {
      console.error("Failed to upload sound:", err);
      alert("Error uploading sound.");
    } finally {
      setUploading(false);
    }
  };
  if (loading) {
    return <SettingsSectionSkeleton titleWidth="w-56" rows={4} />;
  }
  return <div className="mx-auto max-w-4xl space-y-6 text-gray-800 dark:text-gray-200">
            <div className="text-center">
                <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">
                    Sound Notifications
                </h2>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    Configure which sound plays and for how long when key events happen on this station.
                </p>
            </div>

            <div className="space-y-6">
                {EVENT_CONFIGS.map(event => {
        const eventSettings = settings[event.key];
        const isPlaying = previewingKey === event.key;
        return <div key={event.key} className="border-b border-gray-200 pb-6 last:border-b-0 dark:border-gray-700">
                            <div className="space-y-3">
                            <h4 className="font-semibold">{event.label}</h4>

                            {/* Sound dropdown */}
                            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                <label className="text-sm">Sound file:</label>
                                <select value={eventSettings.sound} onChange={e => updateEventSetting(event.key, "sound", e.target.value)} className="flex-1 px-3 py-2 border max-w-sm rounded dark:bg-gray-700 dark:text-white">
                                    <option value="">None</option>
                                    {soundOptions.map(opt => <option key={opt.id || opt.file} value={opt.file}>
                                            {opt.label || opt.file}
                                        </option>)}
                                </select>

                                <button type="button" onClick={() => handlePreview(event.key)} disabled={!eventSettings.sound} className={`px-3 py-2 w-44 rounded text-sm border border-blue-600 text-blue-600 hover:bg-blue-50
                    disabled:opacity-50 disabled:cursor-not-allowed`}>
                                    {isPlaying ? "Stop" : "Preview"}
                                </button>
                            </div>

                            {/* Duration */}
                            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                <label className="text-sm w-full sm:w-40">
                                    Duration (seconds):
                                </label>
                                <input type="number" min={0} step={0.1} value={(eventSettings.durationMs / 1000).toFixed(1).replace(/\.0$/, "")} // show 1 or 1.5
              onChange={e => updateEventSetting(event.key, "durationMs", Math.max(0, Number(e.target.value) * 1000) // store ms again
              )} className="w-40 px-3 py-2 border rounded dark:bg-gray-700 dark:text-white" />
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                    0 = play full sound once. &gt;0 = loop preview for this duration.
                                </span>
                            </div>

                            </div>
                        </div>;
      })}
            </div>

            {/* Upload new sound block */}
            <div className="border-t border-gray-200 pt-6 dark:border-gray-700">
                <h4 className="font-semibold">Upload a new sound</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                    Accepted: audio files (mp3, wav, ogg). Max 5MB. Uploaded sounds will
                    appear in the dropdown above.
                </p>

                <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                    <input type="file" accept="audio/*" onChange={e => setUploadFile(e.target.files?.[0] || null)} className="text-sm" />
                    <button type="button" onClick={handleUpload} disabled={uploading || !uploadFile} className={`px-4 py-2 rounded text-sm bg-gray-700 text-white hover:bg-gray-800
              disabled:opacity-50 disabled:cursor-not-allowed`}>
                        {uploading ? "Uploading..." : "Upload Sound"}
                    </button>
                </div>
            </div>
            {saving && <div className="pt-1 text-center text-xs text-gray-500 dark:text-gray-400">
                    Saving...
                </div>}
        </div>;
}
